import type { Locale } from "./i18n";

export function formatLocalizedNumber(
  value: number,
  locale: Locale,
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}

export function formatLocalizedCurrency(
  value: number,
  locale: Locale,
  currency = "USD",
  options: Intl.NumberFormatOptions = {},
): string {
  return formatLocalizedNumber(value, locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
    ...options,
  });
}

export function formatLocalizedDateTime(
  value: string | Date,
  locale: Locale,
  options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  },
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(locale, options).format(date);
}

export function formatLocalizedPercent(
  value: number,
  locale: Locale,
  maximumFractionDigits = 2,
  signDisplay: Intl.NumberFormatOptions["signDisplay"] = "auto",
): string {
  return formatLocalizedNumber(value / 100, locale, {
    style: "percent",
    maximumFractionDigits,
    signDisplay,
  });
}

export function formatLocalizedDuration(
  startValue: string,
  endValue: string,
  locale: Locale,
): string {
  const start = new Date(startValue).getTime();
  const end = new Date(endValue).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return "—";
  const minutes = Math.max(1, Math.round((end - start) / 60_000));
  if (minutes < 60) {
    return formatLocalizedNumber(minutes, locale, { style: "unit", unit: "minute", unitDisplay: "short" });
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return formatLocalizedNumber(hours, locale, { style: "unit", unit: "hour", unitDisplay: "short" });
  }
  return formatLocalizedNumber(Math.round(hours / 24), locale, { style: "unit", unit: "day", unitDisplay: "short" });
}
